import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD } from "../utils/constants";
import { fmtCD } from "../utils/formatUtils";

const TABS = [
  ["scanner", "SCAN"],
  ["watchlist", "WATCHLIST"],
];

export default function Navbar({
  marketOpen,
  tab, setTab,
  watchlistCount,
  autoScan, countdown,
  firedAlerts, setFiredAlerts,
  settingsOpen, setSettingsOpen,
  user,
  isPro,
  subLoading,
}) {
  const proBadge = isPro || subLoading;
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  if (isMobile) {
    return (
      <>
        {/* Mobile main nav row */}
        <nav className="ts-panel" style={{ position: "sticky", top: 0, zIndex: 300, background: "rgba(5,8,20,0.94)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${BD}`, boxShadow: "0 16px 60px rgba(0,0,0,0.22)" }}>
          <div style={{ height: 50, display: "flex", alignItems: "center", padding: "0 14px", justifyContent: "space-between" }}>
            {/* Brand */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: marketOpen ? GR : RD, boxShadow: `0 0 8px ${marketOpen ? GR : RD}`, animation: "pulse 2s infinite" }} />
              <span className="ts-gradient-text" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 15 }}>Trade Scanner</span>
              <span style={{ fontSize: 7, color: TX2, letterSpacing: 2, border: `1px solid ${BD}`, padding: "1px 5px", borderRadius: 3 }}>v4</span>
            </div>
            {/* Right controls */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <div style={{ background: proBadge ? GR + "18" : YL + "12", border: `1px solid ${proBadge ? GR + "50" : YL + "35"}`, borderRadius: 6, padding: "5px 8px", color: proBadge ? GR : YL, fontSize: 8, fontWeight: 700, letterSpacing: 1 }}>
                {proBadge ? "PRO" : "LOCKED"}
              </div>
              <button className="btn" onClick={() => setSettingsOpen(!settingsOpen)} style={{ background: settingsOpen ? CARD3 : CARD2, border: `1px solid ${BD}`, borderRadius: 6, padding: "5px 9px", color: TX2, fontSize: 11 }}>⚙</button>
              {/* Hamburger */}
              <button
                className="btn"
                onClick={() => setMenuOpen(!menuOpen)}
                style={{ background: menuOpen ? CARD3 : CARD2, border: `1px solid ${BD}`, borderRadius: 6, padding: "5px 9px", color: TX2, fontSize: 13, lineHeight: 1 }}
                aria-label="Menu"
              >
                {menuOpen ? "✕" : "☰"}
              </button>
            </div>
          </div>

          {/* Mobile tab bar */}
          <div style={{ display: "flex", gap: 2, padding: "0 10px 8px" }}>
            <div style={{ display: "flex", gap: 2, background: "rgba(13,23,40,0.92)", border: `1px solid ${BD}`, borderRadius: 8, padding: 2 }}>
              {TABS.map(([id, lbl]) => (
                <button
                  key={id}
                  className="btn"
                  onClick={() => setTab(id)}
                  style={{ background: tab === id ? CARD3 : "transparent", border: `1px solid ${tab === id ? BD : "transparent"}`, borderRadius: 6, padding: "5px 12px", color: tab === id ? TX : TX2, fontSize: 9, fontWeight: tab === id ? 600 : 400, letterSpacing: 1, whiteSpace: "nowrap" }}
                >
                  {lbl}{id === "watchlist" && watchlistCount > 0 ? ` (${watchlistCount})` : ""}
                </button>
              ))}
            </div>
          </div>

          {/* Dropdown menu */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="ts-panel"
                style={{ background: "rgba(5,8,20,0.98)", borderBottom: `1px solid ${BD}`, padding: "12px 14px 16px" }}
              >
                {/* User info */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "10px 12px", background: CARD2, borderRadius: 8, border: `1px solid ${BD}` }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg,${GR},${BL})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#000", flexShrink: 0 }}>
                    {user?.displayName?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: TX }}>{user?.displayName}</div>
                    <div style={{ fontSize: 8, color: TX2 }}>{user?.email}</div>
                  </div>
                </div>
                {/* Alerts */}
                {firedAlerts.length > 0 && (
                  <button className="btn" onClick={() => setFiredAlerts([])} style={{ width: "100%", background: YL + "18", border: `1px solid ${YL}40`, borderRadius: 6, padding: "7px", color: YL, fontSize: 9, marginBottom: 8 }}>
                    {firedAlerts.length} alert{firedAlerts.length > 1 ? "s" : ""} — dismiss all
                  </button>
                )}
                {/* Auto scan countdown */}
                {autoScan && countdown && (
                  <div style={{ fontSize: 8, color: TX2, textAlign: "center", padding: "4px 0" }}>next scan in {fmtCD(countdown)}</div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────────
  return (
    <nav className="ts-panel" style={{ position: "sticky", top: 0, zIndex: 300, background: "rgba(5,8,20,0.94)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${BD}`, height: 50, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, justifyContent: "space-between", boxShadow: "0 16px 60px rgba(0,0,0,0.22)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: marketOpen ? GR : RD, boxShadow: `0 0 8px ${marketOpen ? GR : RD}`, animation: "pulse 2s infinite" }} />
        <span className="ts-gradient-text" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 16 }}>Trade Scanner</span>
        <span style={{ fontSize: 7, color: TX2, letterSpacing: 2, border: `1px solid ${BD}`, padding: "1px 5px", borderRadius: 3 }}>v4</span>
        <div style={{ width: 1, height: 16, background: BD }} />
        <span style={{ fontSize: 9, color: marketOpen ? GR : RD, letterSpacing: 1 }}>{marketOpen ? "LIVE" : "CLOSED"}</span>
      </div>
      <div style={{ display: "flex", gap: 1, background: "rgba(13,23,40,0.92)", border: `1px solid ${BD}`, borderRadius: 8, padding: 2 }}>
        {TABS.map(([id, lbl]) => (
          <button key={id} className="btn" onClick={() => setTab(id)} style={{ background: tab === id ? CARD3 : "transparent", border: `1px solid ${tab === id ? BD : "transparent"}`, borderRadius: 6, padding: "5px 13px", color: tab === id ? TX : TX2, fontSize: 9, fontWeight: tab === id ? 600 : 400, letterSpacing: 1 }}>
            {lbl}{id === "watchlist" && watchlistCount > 0 ? ` (${watchlistCount})` : ""}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {autoScan && countdown && <span style={{ fontSize: 9, color: TX2 }}>next {fmtCD(countdown)}</span>}
        {firedAlerts.length > 0 && <button className="btn" onClick={() => setFiredAlerts([])} style={{ background: YL + "18", border: `1px solid ${YL}40`, borderRadius: 6, padding: "4px 8px", color: YL, fontSize: 9 }}>{firedAlerts.length} alert{firedAlerts.length > 1 ? "s" : ""}</button>}
        <div style={{ background: proBadge ? GR + "18" : YL + "12", border: `1px solid ${proBadge ? GR + "50" : YL + "35"}`, borderRadius: 6, padding: "5px 10px", color: proBadge ? GR : YL, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>
          {proBadge ? "PRO" : "LOCKED"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: CARD2, border: `1px solid ${BD}`, borderRadius: 6, padding: "4px 10px" }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", background: `linear-gradient(135deg,${GR},${BL})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#000", flexShrink: 0 }}>
            {user?.displayName?.[0]?.toUpperCase() || "U"}
          </div>
          <span style={{ fontSize: 9, color: TX2, maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.displayName}</span>
        </div>
        <button className="btn" onClick={() => setSettingsOpen(!settingsOpen)} style={{ background: settingsOpen ? CARD3 : CARD2, border: `1px solid ${BD}`, borderRadius: 6, padding: "5px 9px", color: TX2, fontSize: 11 }}>⚙</button>
      </div>
    </nav>
  );
}
